<template>
  <fieldset class="fieldset mb-4 mb-lg-5">
    <legend>Anbieter / Gastgeber und Standort</legend>

    <Field type="hidden" name="location_id" />
    <Field type="hidden" name="configured_host_object_id" />
    <Field type="hidden" name="host_object_setting" />

    <div class="host-location-options">
      <button
        v-if="canBeHostObject"
        type="button"
        class="host-location-option"
        :class="{'host-location-option--active': selectedMode === 'is_host'}"
        @click="selectIsHost"
      >
        <strong>Diesen Eintrag als Gastgeber verwenden</strong>
        <small>Andere Einträge können diesen Gastgeber verwenden. Dann werden Adresse/Lage dieses Standorts geteilt und beide Einträge werden untereinander verlinkt.</small>
      </button>

      <button
        type="button"
        class="host-location-option"
        :class="{'host-location-option--active': selectedMode === 'has_host'}"
        @click="selectHasHost"
      >
        <strong>Dieser Eintrag hat einen Gastgeber</strong>
        <small>Dieser Eintrag und der Gastgeber werden untereinander verlinkt. Die Adresse/Lage wird vom Gastgeber übernommen.</small>
      </button>

      <button
        type="button"
        class="host-location-option"
        :class="{'host-location-option--active': selectedMode === 'no_host'}"
        @click="selectNoHost"
      >
        <strong>Dieser Eintrag soll mit keinem Gastgeber verknüpft werden</strong>
        <small>Es wird keine Eintrags-Verlinkung angelegt. Vom ausgewählten Standort wird nur die Adresse/Lage verwendet.</small>
      </button>
    </div>

    <div v-if="selectedMode === 'has_host'" class="mt-4">
      <h3 class="h5 mb-2">Gastgeber auswählen</h3>
      <div v-if="hostObjects.length" class="host-location-choices">
        <button
          v-for="hostObject in hostObjects"
          :key="`host-${hostObject.id}`"
          type="button"
          class="host-location-choice"
          :class="{'host-location-choice--active': Number(selectedHostObjectId) === Number(hostObject.id)}"
          @click="handleHostSelection(hostObject.id)"
        >
          <span>
            <strong>{{ hostObject.name }}</strong>
            <small>{{ hostObjectTypeLabel(hostObject) }}<template v-if="hostObjectLocationLabel(hostObject)"> · {{ hostObjectLocationLabel(hostObject) }}</template></small>
          </span>
        </button>
      </div>
      <div v-else class="alert alert-info">
        Derzeit ist kein Anbieter / Gastgeber verfügbar.
      </div>
      <ErrorMessage name="configured_host_object_id" :class="errorMessageClasses" />
    </div>

    <div v-else class="host-location-section mt-4">
      <h3 class="h5 mb-2">Standort auswählen</h3>

      <div v-if="hasManyLocations" class="mb-3">
        <label for="host-location-search" class="form-label">Standorte durchsuchen</label>
        <input
          id="host-location-search"
          v-model="locationSearch"
          type="search"
          class="form-control"
          autocomplete="off"
          placeholder="Name oder Adresse"
          @input="scheduleLocationSearch"
        />
      </div>

      <div v-if="displayedLocations.length" class="host-location-choices">
        <button
          v-for="location in displayedLocations"
          :key="`location-${location.id}`"
          type="button"
          class="host-location-choice"
          :class="{'host-location-choice--active': Number(selectedLocationId) === Number(location.id)}"
          @click="handleLocationSelection(location)"
        >
          <span>
            <strong>{{ location.name }}</strong>
            <small>{{ locationAddressLabel(location) }}</small>
          </span>
        </button>
      </div>
      <p v-else-if="locationSearch" class="text-muted small">Kein passender eigener Standort gefunden.</p>

      <button type="button" class="btn btn-secondary mt-3" @click="startAddingLocation">
        Standort hinzufügen
      </button>

      <div v-if="addingLocation" class="mt-4">
        <Standort
          :key="locationEditorKey"
          :errors="errors"
          :model-value="null"
          @update:modelValue="handleAddressChange"
          @confirmed="finishNewLocation"
        />
      </div>

      <ErrorMessage name="location_id" :class="errorMessageClasses" />
    </div>
  </fieldset>
</template>

<script>
import {ErrorMessage, Field} from 'vee-validate';
import Standort from '@/components/general/Standort.vue';
import {useUserStore} from '@/stores/user';
import axios from 'axios';
import {
  buildPhotonSearchUrl,
  filterPhotonHouseResults,
  mapPhotonFeatureToAddress,
  shouldSearchPhoton,
} from '@/scripts/locationSearch';

function canUseHostObject(baseType) {
  return ['unterkunft', 'entdecken'].includes(baseType)
}

export default {
  name: 'HostLocationSelector',
  components: {
    ErrorMessage,
    Field,
    Standort,
  },
  props: {
    baseType: {
      type: String,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    values: {
      type: Object,
      default: () => ({}),
    },
    initialValues: {
      type: Object,
      required: true,
    },
    hostObjects: {
      type: Array,
      default: () => [],
    },
    setFieldValue: {
      type: Function,
      required: true,
    },
    setFieldError: {
      type: Function,
      required: true,
    },
  },
  emits: ['location-change'],
  setup() {
    return {
      userStore: useUserStore(),
    }
  },
  data() {
    return {
      selectedMode: this.initialMode(),
      selectedHostObjectId: this.initialValues.configured_host_object_id ?? null,
      selectedLocationId: this.initialValues.location_id ?? null,
      selectedLocation: this.initialValues.location ? {...this.initialValues.location} : null,
      addingLocation: false,
      locationEditorKey: 0,
      locationSearch: '',
      locationSearchResults: [],
      locationSearchTimer: null,
      errorMessageClasses: 'mt-1 d-block invalid-feedback',
    }
  },
  computed: {
    canBeHostObject() {
      return canUseHostObject(this.baseType)
    },
    hasManyLocations() {
      return this.userStore.myLocationsTotal > 7 || this.userStore.myLocations.length > 7
    },
    displayedLocations() {
      let locations
      if (this.locationSearch.trim()) {
        locations = this.locationSearchResults
      } else if (this.hasManyLocations) {
        locations = this.userStore.myLocations.slice(0, 7)
      } else {
        locations = this.userStore.myLocations
      }

      const selectedLocation = this.userStore.myLocations.find(
        (location) => Number(location.id) === Number(this.selectedLocationId)
      )
      if (selectedLocation && !locations.some((location) => Number(location.id) === Number(selectedLocation.id))) {
        return [selectedLocation, ...locations]
      }
      return locations
    },
  },
  mounted() {
    this.userStore.fetchMyLocations().catch((error) => {
      console.error('fetchMyLocations failed', error)
    })
    this.resetFromInitialValues()
  },
  beforeUnmount() {
    if (this.locationSearchTimer) {
      clearTimeout(this.locationSearchTimer)
    }
  },
  methods: {
    initialMode() {
      if (this.initialValues.configured_host_object_id) {
        return 'has_host'
      }
      if (canUseHostObject(this.baseType) && this.isTruthyValue(this.initialValues.host_object_setting)) {
        return 'is_host'
      }
      return 'no_host'
    },
    isTruthyValue(value) {
      return value === true || value === 'true' || value === 1 || value === '1'
    },
    resetFromInitialValues() {
      this.selectedMode = this.initialMode()
      this.selectedHostObjectId = this.initialValues.configured_host_object_id ?? null
      this.selectedLocationId = this.initialValues.location_id ?? null
      this.selectedLocation = this.initialValues.location ? {...this.initialValues.location} : null
      this.addingLocation = false
      this.locationSearch = ''
      this.locationSearchResults = []
      this.applyModeToFields()
    },
    applyModeToFields() {
      if (this.selectedMode === 'is_host') {
        this.setFieldValue('host_object_setting', true, false)
        this.setFieldValue('configured_host_object_id', null, false)
      } else if (this.selectedMode === 'has_host') {
        this.setFieldValue('host_object_setting', false, false)
        this.setFieldValue('configured_host_object_id', this.selectedHostObjectId, false)
      } else {
        this.setFieldValue('host_object_setting', false, false)
        this.setFieldValue('configured_host_object_id', null, false)
      }
      this.setFieldValue('location_id', this.selectedLocationId, false)
    },
    selectIsHost() {
      if (!this.canBeHostObject) return

      this.selectedMode = 'is_host'
      this.selectedHostObjectId = null
      this.setFieldValue('host_object_setting', true, true)
      this.setFieldValue('configured_host_object_id', null, true)
      this.setFieldValue('location_id', this.selectedLocationId, true)
      this.clearHostErrors()
    },
    selectHasHost() {
      this.selectedMode = 'has_host'
      this.setFieldValue('host_object_setting', false, true)
      this.setFieldValue('configured_host_object_id', this.selectedHostObjectId, true)
      this.clearLocationErrors()
    },
    selectNoHost() {
      this.selectedMode = 'no_host'
      this.selectedHostObjectId = null
      this.setFieldValue('host_object_setting', false, true)
      this.setFieldValue('configured_host_object_id', null, true)
      this.setFieldValue('location_id', this.selectedLocationId, true)
      this.clearHostErrors()
    },
    handleHostSelection(rawHostObjectId) {
      const hostObjectId = rawHostObjectId ? Number(rawHostObjectId) : null
      const hostObject = this.hostObjectById(hostObjectId)
      this.selectedMode = 'has_host'
      this.selectedHostObjectId = hostObjectId
      this.selectedLocationId = hostObject?.location?.id ?? null
      this.selectedLocation = hostObject?.location
        ? this.normalizeLocationFromAddress(hostObject.location, this.selectedLocationId)
        : null
      this.setFieldValue('host_object_setting', false, true)
      this.setFieldValue('configured_host_object_id', hostObjectId, true)
      this.setFieldValue('location_id', this.selectedLocationId, true)
      this.setFieldValue('search', '', false)
      this.setFieldValue('address_name', '', false)
      this.clearHostErrors()
      this.clearLocationErrors()
      this.addingLocation = false
      this.emitLocationChange()
    },
    handleLocationSelection(location) {
      const locationId = Number(location.id)
      this.selectedHostObjectId = null
      this.selectedLocationId = locationId
      this.selectedLocation = this.normalizeLocationFromAddress(location, locationId)
      this.setFieldValue('configured_host_object_id', null, true)
      this.setFieldValue('location_id', locationId, true)
      this.setFieldValue('search', location.name || this.locationAddressLabel(location), false)
      this.setFieldValue('address_name', location.name || '', false)
      this.clearHostErrors()
      this.clearLocationErrors()
      this.addingLocation = false
      this.emitLocationChange()
    },
    startAddingLocation() {
      this.addingLocation = true
      this.locationEditorKey += 1
      this.setFieldValue('search', '', false)
      this.setFieldError('location_id', undefined)
    },
    handleAddressChange(newAddress) {
      if (!newAddress?.id && !newAddress?.street) {
        this.selectedLocationId = null
        this.selectedLocation = null
        this.setFieldValue('location_id', null, true)
        this.emitLocationChange()
        return
      }

      const locationId = newAddress.id ? Number(newAddress.id) : null
      this.selectedHostObjectId = null
      this.selectedLocationId = locationId
      this.selectedLocation = this.normalizeLocationFromAddress(newAddress, locationId)
      this.setFieldValue('configured_host_object_id', null, true)
      this.setFieldValue('location_id', locationId, true)
      this.setFieldValue('address_name', newAddress.name || '', false)
      this.clearLocationErrors()
      this.emitLocationChange()
    },
    async finishNewLocation(confirmedAddress = this.selectedLocation) {
      if (confirmedAddress?.street) {
        this.selectedLocation = this.normalizeLocationFromAddress(confirmedAddress, confirmedAddress.id ? Number(confirmedAddress.id) : null)
        this.selectedLocationId = this.selectedLocation.id
      }
      if (!this.selectedLocation?.street || !String(this.selectedLocation?.name || '').trim()) {
        return
      }

      if (!this.selectedLocationId) {
        const created = await this.userStore.createLocationFromAddress(this.createAddressPayloadFromLocation(this.selectedLocation))
        if (!created?.id) {
          this.setFieldError('location_id', 'Standort konnte nicht gespeichert werden.')
          return
        }

        this.selectedLocationId = Number(created.id)
        this.selectedLocation = this.normalizeLocationFromAddress(created, this.selectedLocationId)
        this.setFieldValue('location_id', this.selectedLocationId, true)
        this.setFieldValue('address_name', this.selectedLocation.name || '', false)
        this.userStore.fetchMyLocations(true)
        this.emitLocationChange()
      }

      this.selectedHostObjectId = null
      this.setFieldValue('configured_host_object_id', null, true)
      this.clearHostErrors()
      this.clearLocationErrors()
      this.addingLocation = false
    },
    scheduleLocationSearch() {
      if (this.locationSearchTimer) {
        clearTimeout(this.locationSearchTimer)
      }

      const query = this.locationSearch.trim()
      if (!query) {
        this.locationSearchResults = []
        return
      }

      this.locationSearchTimer = setTimeout(() => {
        this.userStore.searchMyLocations(query).then((locations) => {
          if (this.locationSearch.trim() === query) {
            this.locationSearchResults = locations
          }
        }).catch((error) => {
          console.error('searchMyLocations failed', error)
        })
      }, 250)
    },
    async prepareSubmit(values, {requireLocation = true} = {}) {
      if (this.selectedMode === 'has_host') {
        if (!this.selectedHostObjectId) {
          if (requireLocation) {
            return {ok: false, errors: {configured_host_object_id: 'Bitte Anbieter / Gastgeber auswählen.'}}
          }
          values.host_object_setting = false
          values.configured_host_object_id = null
          values.location_id = this.selectedLocationId ?? null
          return {ok: true}
        }

        const hostObject = this.hostObjectById(this.selectedHostObjectId)
        values.host_object_setting = false
        values.configured_host_object_id = Number(this.selectedHostObjectId)
        values.location_id = hostObject?.location?.id ?? this.selectedLocationId ?? null
        return {ok: true}
      }

      values.host_object_setting = this.selectedMode === 'is_host' && this.canBeHostObject
      values.configured_host_object_id = null
      const locationId = await this.ensureStandaloneLocationForSubmit(values, {allowSearch: requireLocation})
      if (!locationId && requireLocation) {
        return {ok: false, errors: {location_id: 'Bitte Standort auswählen oder Adresse auswählen.'}}
      }

      values.location_id = locationId ?? null
      return {ok: true}
    },
    async ensureStandaloneLocationForSubmit(values, {allowSearch = true} = {}) {
      const submittedLocationName = String(values.address_name || '').trim()
      const existingLocationName = String(this.selectedLocation?.name || '').trim()
      const shouldKeepExistingLocation = values.location_id
        && (!submittedLocationName || submittedLocationName === existingLocationName)

      if (shouldKeepExistingLocation) {
        return Number(values.location_id)
      }

      const location = this.selectedLocation
        ? {
          ...this.selectedLocation,
          name: submittedLocationName || this.selectedLocation.name,
        }
        : allowSearch ? await this.resolveLocationFromSearch(values) : null
      if (!location?.street || !String(location?.name || '').trim()) {
        return null
      }

      const created = await this.userStore.createLocationFromAddress(this.createAddressPayloadFromLocation(location))
      if (!created?.id) {
        return null
      }

      const locationId = Number(created.id)
      this.selectedLocationId = locationId
      this.selectedLocation = this.normalizeLocationFromAddress(created, locationId)
      values.location_id = locationId
      this.userStore.fetchMyLocations(true)
      this.emitLocationChange()
      return locationId
    },
    async resolveLocationFromSearch(values) {
      const query = String(values.search || '').trim()
      if (!shouldSearchPhoton(query)) {
        return null
      }

      const {data} = await axios.get(buildPhotonSearchUrl(query))
      const [feature] = filterPhotonHouseResults(data.features)
      if (!feature) {
        return null
      }

      const address = mapPhotonFeatureToAddress(feature)
      return this.normalizeLocationFromAddress({
        ...address,
        name: values.address_name || values.name || address.street,
      })
    },
    createAddressPayloadFromLocation(location) {
      return {
        name: location.name,
        street: location.street,
        postcode: location.zip,
        city: location.town,
        country: location.country,
        lat: location.latitude,
        lon: location.longitude,
      }
    },
    normalizeLocationFromAddress(address, id = address?.id ?? null) {
      return {
        id,
        name: address.name,
        street: address.street,
        zip: address.zip ?? address.postcode,
        town: address.town ?? address.city,
        country: address.country,
        latitude: address.latitude ?? address.lat,
        longitude: address.longitude ?? address.lon,
      }
    },
    hostObjectById(id) {
      return this.hostObjects.find((hostObject) => Number(hostObject.id) === Number(id))
    },
    hostObjectTypeLabel(hostObject) {
      return hostObject?.base_type === 'unterkunft' ? 'Unterkunft' : 'Entdecken'
    },
    hostObjectLocationLabel(hostObject) {
      const location = hostObject?.location
      if (!location) return ''

      return [location.name, location.town].filter(Boolean).join(', ')
    },
    locationAddressLabel(location) {
      if (!location) return ''

      const zip = location.zip ?? location.postcode
      const town = location.town ?? location.city
      return [location.street, [zip, town].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    },
    clearHostErrors() {
      this.setFieldError('configured_host_object_id', undefined)
    },
    clearLocationErrors() {
      this.setFieldError('location_id', undefined)
      this.setFieldError('search', undefined)
      this.setFieldError('address_name', undefined)
    },
    emitLocationChange() {
      this.$emit('location-change', {
        id: this.selectedLocationId,
        location: this.selectedLocation ? {...this.selectedLocation} : null,
      })
    },
  },
}
</script>

<style scoped>
.host-location-options,
.host-location-choices {
  display: grid;
  gap: 0.75rem;
}

.host-location-option,
.host-location-choice {
  display: flex;
  width: 100%;
  gap: 0.75rem;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  text-align: left;
  background: #fff;
  border: 1px solid #d8dee5;
  border-radius: 0;
}

.host-location-option small,
.host-location-choice small {
  display: block;
  margin-top: 0.2rem;
  color: #6c757d;
}

.host-location-option--active,
.host-location-choice--active {
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.16rem rgba(13, 110, 253, 0.14);
}
</style>
